import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, onStepUpRequired } from '../lib/api'
import { useT } from '../lib/i18n'
import { sendMagicLink, useMe } from '../lib/session'
import { ErrorNote, Modal } from './ui'

/**
 * Step-up: money actions need a recent sign-in. With TOTP the code proves it in place;
 * otherwise a fresh magic link brings the user back to the same page.
 */
export function StepUp() {
  const { t } = useT()
  const me = useMe()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  useEffect(
    () =>
      onStepUpRequired(() => {
        setSent(false)
        setCode('')
        setOpen(true)
      }),
    [],
  )
  const mfa = useQuery({
    queryKey: ['mfa'],
    queryFn: () => api<{ factors: { id: string; status: string }[] }>('/api/auth/mfa'),
    enabled: open,
  })
  const factor = mfa.data?.factors.find((f) => f.status === 'verified')
  const verify = useMutation({
    mutationFn: () => api('/api/auth/mfa/verify', { body: { factorId: factor?.id, code } }),
    onSuccess: async () => {
      setOpen(false)
      await qc.invalidateQueries()
    },
  })
  const email = me.data?.user?.email
  return (
    <Modal open={open} onClose={() => setOpen(false)} title={t('stepUpTitle')}>
      {factor ? (
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            verify.mutate()
          }}
        >
          <p className="text-sm text-body">{t('stepUpTotp')}</p>
          <input
            className="input mono text-center text-2xl tracking-[0.4em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            aria-label={t('mfaCode')}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <button type="submit" className="btn btn-primary" disabled={code.length !== 6 || verify.isPending}>
            {t('verify')}
          </button>
          <ErrorNote error={verify.error} />
        </form>
      ) : (
        <>
          <p className="mb-6 text-sm text-body">
            {sent ? t('checkInboxText', { email: email ?? '' }) : t('stepUpText')}
          </p>
          {!sent && email && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() =>
                sendMagicLink(email, location.pathname + location.search).then(() => setSent(true))
              }
            >
              {t('sendLink')}
            </button>
          )}
        </>
      )}
    </Modal>
  )
}
